import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Calendar, Clock, User, Mail, Phone, CheckCircle, ArrowLeft, Sparkles, Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const BookingPage = () => {
  const { t, i18n } = useTranslation();
  const { username } = useParams();
  const [provider, setProvider] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showLangMenu, setShowLangMenu] = useState(false);
  const languageMenuRef = useRef(null);
  const VITE_SERVER_URL = import.meta.env.VITE_SERVER_URL || 'https://easyrdv.onrender.com';

  console.log(provider);
  
  const [formData, setFormData] = useState({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    notes: ''
  });

  const [phoneError, setPhoneError] = useState('');

  useEffect(() => {
    fetchProviderInfo();
  }, [username]);

  useEffect(() => {
    if (selectedDate) {
      fetchAvailableSlots();
    }
  }, [selectedDate]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (languageMenuRef.current && !languageMenuRef.current.contains(event.target)) {
        setShowLangMenu(false);
      }
    };

    if (showLangMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showLangMenu]);

  const fetchProviderInfo = async () => {
    try {
      // Fetch user info by username
      const response = await axios.get(`${VITE_SERVER_URL}/api/auth/user/${username}`);
      const userData = response.data.data.user;
      setProvider({
        name: userData.name,
        username: userData.username,
        profession: userData.profession,
      });
      setLoading(false);
    } catch (error) {
      console.error('Error fetching provider info:', error);
      setError('Professionnel non trouvé');
      setLoading(false);
    }
  };

  const fetchAvailableSlots = async () => {
    try {
      const response = await axios.get(`${VITE_SERVER_URL}/api/public/${username}/slots`, {
        params: { date: selectedDate }
      });
      setAvailableSlots(response.data.data.availableSlots);
    } catch (error) {
      console.error('Erreur lors de la récupération des créneaux:', error);
      setAvailableSlots([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBookingLoading(true);
    setError('');

    try {
      await axios.post(`${VITE_SERVER_URL}/api/public/${username}/appointments`, {
        ...formData,
        date: selectedDate,
        time: selectedTime
      });

      setSuccess(true);
      setFormData({
        customerName: '',
        customerEmail: '',
        customerPhone: '',
        notes: ''
      });
      setSelectedDate('');
      setSelectedTime('');
    } catch (error) {
      setError(error.response?.data?.message || 'Erreur lors de la réservation');
    } finally {
      setBookingLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData({
      ...formData,
      [name]: value
    });

    // Phone number validation
    if (name === 'customerPhone') {
      if (value && !/^[0-9+\-\s()]+$/.test(value)) {
        setPhoneError('Le numéro de téléphone ne doit contenir que des chiffres, espaces, tirets, parenthèses et le signe +');
      } else {
        setPhoneError('');
      }
    }
  };

  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    setShowLangMenu(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-purple-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!provider) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-purple-50">
        <div className="text-center bg-white rounded-2xl p-8 shadow-lg">
          <div className="w-16 h-16 bg-gradient-to-r from-red-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <User className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">{t('booking.errors.noSlots')}</h1>
          <p className="text-gray-600">{t('booking.errors.bookingFailed')}</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-purple-50">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <div className="w-20 h-20 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            {t('booking.success.title')}
          </h1>
          <p className="text-gray-600 mb-6 leading-relaxed">
            {t('booking.success.message')}
          </p>
          <p className="text-gray-500 text-sm mb-8 leading-relaxed">
            {t('booking.success.checkEmail')}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white px-8 py-3 rounded-xl font-semibold hover:from-indigo-600 hover:to-purple-600 transition-all duration-300 transform hover:scale-105 shadow-lg"
          >
            {t('booking.actions.book')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gradient-to-br from-indigo-50 to-purple-50 ${i18n.language === 'ar' ? 'rtl' : 'ltr'}`}>
      {/* Floating Language Switcher */}
      {/* Mobile: Show button, open card on click. Desktop: Show card always. */}
      <div>
        {/* Mobile Button */}
        <button
          className={`fixed top-2 ${i18n.language === 'ar' ? 'left-2' : 'right-2'} z-50 bg-white border-2 border-indigo-200 rounded-full shadow-lg flex items-center gap-2 px-4 py-2 sm:hidden`}
          onClick={() => setShowLangMenu(true)}
          aria-label={t('language.label')}
        >
          <Globe className="w-7 h-7 text-indigo-600" />
          <span className="font-bold text-base">{t('language.label')}</span>
        </button>

        {/* Language Card (modal on mobile, always on desktop) */}
        {(showLangMenu || window.innerWidth >= 640) && (
          <div
            className={`fixed z-50 bg-white border-2 border-indigo-200 rounded-2xl shadow-lg flex flex-col items-center py-4 px-6
              max-w-xs w-full
              ${i18n.language === 'ar' ? 'left-2' : 'right-2'} top-2
              sm:top-4 sm:left-auto sm:right-auto sm:${i18n.language === 'ar' ? 'left-4' : 'right-4'}`}
            style={{ minWidth: 'unset' }}
          >
            {/* Close button for mobile modal */}
            <button
              className="absolute top-2 right-2 sm:hidden text-gray-400 hover:text-gray-700 text-2xl font-bold"
              onClick={() => setShowLangMenu(false)}
              aria-label={t('common.close')}
            >
              ×
            </button>
            <Globe className="w-10 h-10 text-indigo-600 mb-2 sm:w-10 sm:h-10 w-7 h-7" />
            <span className="font-bold text-xl mb-4 sm:text-xl text-base mb-2">{t('language.label')}</span>
            <button
              onClick={() => { changeLanguage('fr'); setShowLangMenu(false); }}
              className={`w-full mb-2 py-3 rounded-lg text-lg font-bold border-2 transition-colors duration-200
                sm:text-lg text-base py-2
                ${i18n.language === 'fr' ? 'bg-indigo-600 text-white border-indigo-700' : 'bg-gray-100 text-gray-800 border-gray-300 hover:bg-indigo-50'}`}
              aria-label="Français"
            >
              🇫🇷 {t('language.french')}
              {i18n.language === 'fr' && <span className="ml-2">✔</span>}
            </button>
            <button
              onClick={() => { changeLanguage('ar'); setShowLangMenu(false); }}
              className={`w-full py-3 rounded-lg text-lg font-bold border-2 transition-colors duration-200
                sm:text-lg text-base py-2
                ${i18n.language === 'ar' ? 'bg-indigo-600 text-white border-indigo-700' : 'bg-gray-100 text-gray-800 border-gray-300 hover:bg-indigo-50'}`}
              aria-label="العربية"
            >
              🇸🇦 {t('language.arabic')}
              {i18n.language === 'ar' && <span className="ml-2">✔</span>}
            </button>
          </div>
        )}
      </div>
      
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <div className="text-center">
            <div className="w-20 h-20 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <Calendar className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              {t('booking.title')} {provider.name}
            </h1>
            <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-indigo-100 to-purple-100 text-indigo-700 rounded-full text-sm font-medium mb-4">
              <Sparkles className="w-4 h-4 mr-2" />
              {provider.profession}
            </div>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {t('booking.selectDate')}
            </p>
          </div>
        </div>

        {/* Formulaire de réservation */}
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <form onSubmit={handleSubmit} className="space-y-8">
            {error && (
              <div className="bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 text-red-700 px-6 py-4 rounded-xl">
                {error}
              </div>
            )}

            {/* Sélection de la date */}
            <div>
              <label htmlFor="date" className="block text-lg font-semibold text-gray-900 mb-3">
                {t('booking.fields.date')}
              </label>
              <input
                type="date"
                id="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                min={getMinDate()}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-lg"
              />
            </div>

            {/* Sélection de l'heure */}
            {selectedDate && (
              <div>
                <label htmlFor="time" className="block text-lg font-semibold text-gray-900 mb-3">
                  {t('booking.fields.time')}
                </label>
                {availableSlots.length === 0 ? (
                  <div className="text-center py-8 bg-gradient-to-r from-gray-50 to-indigo-50 rounded-xl border border-gray-200">
                    <Clock className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <p className="text-gray-600 font-semibold text-lg">
                      {t('booking.errors.noSlots')}
                    </p>
                    <p className="text-gray-500 mt-2">
                      {t('booking.errors.bookingFailed')}
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {availableSlots.map((slot) => (
                      <button
                        key={slot}
                        type="button"
                        onClick={() => setSelectedTime(slot)}
                        className={`p-4 text-center rounded-xl border-2 transition-all duration-300 font-medium ${
                          selectedTime === slot
                            ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white border-indigo-500 shadow-lg transform scale-105'
                            : 'bg-white text-gray-700 border-gray-200 hover:border-indigo-300 hover:bg-indigo-50'
                        }`}
                      >
                        {slot}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Informations personnelles */}
            <div className="space-y-6">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-8 h-8 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-lg flex items-center justify-center">
                  <User className="w-4 h-4 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">{t('booking.clientInfo')}</h3>
              </div>

              <div>
                <label htmlFor="customerName" className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('booking.fields.name')} *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    id="customerName"
                    name="customerName"
                    value={formData.customerName}
                    onChange={handleChange}
                    required
                    className="appearance-none relative block w-full pl-12 pr-4 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder={t('booking.fields.name')}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="customerEmail" className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('booking.fields.email')} *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="email"
                    id="customerEmail"
                    name="customerEmail"
                    value={formData.customerEmail}
                    onChange={handleChange}
                    required
                    className="appearance-none relative block w-full pl-12 pr-4 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="votre@email.com"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="customerPhone" className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('booking.fields.phone')} ({t('common.optional')})
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Phone className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="tel"
                    id="customerPhone"
                    name="customerPhone"
                    value={formData.customerPhone}
                    onChange={handleChange}
                    className={`appearance-none relative block w-full pl-12 pr-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 placeholder-gray-500 text-gray-900 ${
                      phoneError ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder={t('booking.fields.phone')}
                  />
                </div>
                {phoneError && (
                  <p className="mt-2 text-sm text-red-600">
                    {phoneError}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="notes" className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('booking.fields.notes')} ({t('common.optional')})
                </label>
                <textarea
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows={4}
                  className="block w-full border border-gray-300 rounded-xl px-4 py-3 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Informations supplémentaires..."
                />
              </div>
            </div>

            {/* Bouton de réservation */}
            <button
              type="submit"
              disabled={!selectedDate || !selectedTime || bookingLoading || phoneError}
              className="w-full bg-gradient-to-r from-indigo-500 to-purple-500 text-white py-4 px-6 rounded-xl font-bold hover:from-indigo-600 hover:to-purple-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-105 shadow-lg"
            >
              {bookingLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                  {t('common.loading')}
                </div>
              ) : (
                t('booking.actions.confirm')
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BookingPage; 